import {
  PrivacySettings,
  type Customer,
  type Match,
  type MatchDay,
  type MatchStatusType,
  type Stream,
  type StreamCameraInputStatus,
  type StreamConfig,
} from 'types'

import {
  STREAM_DESCRIPTION_LENGTH,
  STREAM_TEAM_NAME_LENGTH,
  STREAM_TEAM_SHORTNAME_LENGTH,
  STREAM_TITLE_LENGTH,
} from 'settings'

import type { StreamMatchPair } from 'components/week-display/week-display'

type StreamShellProps = {
  customer: Customer
  match: Match
  matchStart: Date
  matchEnd: Date
  gfxConfigId: number | null
  gfxConfigName: string | null
  template: Stream | null
}

export function createStreamShell({
  customer,
  match,
  matchStart,
  matchEnd,
  gfxConfigId,
  gfxConfigName,
  template,
}: StreamShellProps): Stream | null {
  if (!customer.customerId || !customer.customerGuid) return null
  const title = match.title.substring(0, STREAM_TITLE_LENGTH[1])
  const description = match.description.substring(0, STREAM_DESCRIPTION_LENGTH[1])

  return {
    autoStart: true,
    autoTerminate: true,
    cameras: [],
    customerId: customer.customerId,
    description,
    scheduledEndUtc: matchEnd.toISOString(),
    scheduledStartUtc: matchStart.toISOString(),
    streamConfig: {
      autoStart: true,
      autoTerminate: true,
      cameras: [],
      compMatch: {
        ...match,
        title,
        description,
      },
      countryCode: null,
      customerGuid: customer.customerGuid,
      dataProviderPrivacy: match.dataProviderPrivacy ?? 0,
      description,
      destinations: [
        {
          id: 1,
          broadCastID: null,
          provider: 1, // 1=YouTube
          rtmpEndPoint: null,
          status: 0,
          streamID: null,
          streamKey: null,
          utcStarted: null,
          videoURL: null,
        },
      ],
      fbgfxconfigID: template?.streamConfig?.fbgfxconfigID ?? gfxConfigId ?? -1,
      gfxConfigName: template?.streamConfig?.gfxConfigName ?? gfxConfigName ?? null,
      gfxProvider: 4, // 4=FBX Graphics, 2=LIGR
      gfxUrl: null,
      isPrivate: PrivacySettings[match.dataProviderPrivacy ?? 0] === 'PRIVATE',
      isVMSimulation: false,
      mixProvider: 0, // 0=FBX Mix, 3=CloudMix
      publishStatus: template?.streamConfig?.publishStatus ?? match.defaultPublishStatus ?? 0,
      recordingBucketName: null,
      recordingID: null,
      resolution: 0,
      scheduledEndUTC: matchEnd.toISOString(),
      scheduledStartUTC: matchStart.toISOString(),
      slateURL: null,
      sourceRecordingStart: null,
      status: 0,
      title,
      vmExclusive: false,
    },
    title,
  }
}

const matchStatusAsText: Record<number, MatchStatusType> = {
  0: 'upcoming',
  10: 'live',
  20: 'completed',
  30: 'completed',
  35: 'completed',
  40: 'completed',
  50: 'completed',
  60: 'completed',
} as const

export const getMatchStreamStatus = (matchDay?: MatchDay, stream?: Stream): MatchStatusType => {
  if (stream && stream.statusFlags !== undefined) {
    if (stream.statusFlags & 1) {
      if (stream.destinations && stream.destinations[0].status === 1) return 'live'
      return 'preparing'
    }
    if (stream.statusFlags & 2) return 'completed'
    if (stream.statusFlags & 4) return 'scheduled'
  }
  if (matchDay) return matchStatusAsText[matchDay.statusID ?? 60]
  return 'upcoming' // "upcoming" is the fallback display
}

export const isValidStreamConfig = (streamConfig?: StreamConfig) =>
  !(
    streamConfig?.cameras?.length === 0 ||
    (streamConfig?.title ?? '').length < STREAM_TITLE_LENGTH[0] ||
    (streamConfig?.title ?? '').length > STREAM_TITLE_LENGTH[1] ||
    (streamConfig?.description ?? '').length < STREAM_DESCRIPTION_LENGTH[0] ||
    (streamConfig?.description ?? '').length > STREAM_DESCRIPTION_LENGTH[1] ||
    (streamConfig?.compMatch?.homeTeam.teamName ?? '').length < STREAM_TEAM_NAME_LENGTH[0] ||
    (streamConfig?.compMatch?.homeTeam.teamName ?? '').length > STREAM_TEAM_NAME_LENGTH[1] ||
    (streamConfig?.compMatch?.awayTeam.teamName ?? '').length < STREAM_TEAM_NAME_LENGTH[0] ||
    (streamConfig?.compMatch?.awayTeam.teamName ?? '').length > STREAM_TEAM_NAME_LENGTH[1] ||
    (streamConfig?.compMatch?.homeTeam.teamShortName ?? '').length < STREAM_TEAM_SHORTNAME_LENGTH[0] ||
    (streamConfig?.compMatch?.homeTeam.teamShortName ?? '').length > STREAM_TEAM_SHORTNAME_LENGTH[1] ||
    (streamConfig?.compMatch?.awayTeam.teamShortName ?? '').length < STREAM_TEAM_SHORTNAME_LENGTH[0] ||
    (streamConfig?.compMatch?.awayTeam.teamShortName ?? '').length > STREAM_TEAM_SHORTNAME_LENGTH[1]
  )

export const isValidStreamLength = (startDate?: string, endDate?: string) => {
  // checks to ensure stream length is between 1 and 9 hours in duration
  if (!startDate || !endDate || startDate >= endDate) return false
  const startDateTime = new Date(startDate).getTime()
  const endDateTime = new Date(endDate).getTime()
  return endDateTime - startDateTime >= 3600000 && endDateTime - startDateTime <= 32400000
}

export const getFilteredNonDeletedStreams = (matchId: string, streams?: Stream[]): Stream[] =>
  streams?.filter(
    stream =>
      stream.externalId === matchId &&
      (!stream.status || (stream.status && stream.status !== 101 && stream.status !== 102))
  ) ?? []

export const getStreamMatchPairs = (streams: Stream[], matches: Match[]): StreamMatchPair[] =>
  streams.map(stream => {
    const date = stream.streamConfig?.scheduledStartUTC ?? ''
    const match = matches.find(m => m.id === stream.externalId)
    return !match
      ? { date, stream, status: 'match_deleted' }
      : { date, stream, match, status: match.matchDays?.find(day => day.dateTimeUTC === date) ? 'ok' : 'match_moved' }
  })

// checks if input status is either RECEIVING or RECEIVING_BLANK
export const checkCameraInputStatusIsReceiving = (status: StreamCameraInputStatus) => status & 1 || status & 4
